import * as zustand from 'zustand';

// Shape of the store
export const useAuthStore = zustand.create((set, get) => ({
  // --- State ---
  loading: true,
  authenticated: false,
  user: null,
  roles: [],
  permissions: [],
  forbidden: false, // 403 indicator
  error: null,

  // --- Actions ---
  /**
   * Load auth from server
   */
  loadAuth: async () => {
    set({ loading: true, error: null, forbidden: false });

    try {
      const res = await fetch('/api/user/me', {
        credentials: 'include', // send cookies/session
      });

      if (res.status === 403) {
        // Authenticated but forbidden
        set({
          loading: false,
          authenticated: true,
          user: null,
          roles: [],
          permissions: [],
          forbidden: true,
        });
        return;
      }

      if (!res.ok) {
        // Other failures, treat as unauthenticated
        set({
          loading: false,
          authenticated: false,
          user: null,
          roles: [],
          permissions: [],
          forbidden: false,
        });
        return;
      }

      const data = await res.json();
      set({
        loading: false,
        authenticated: !!data.authenticated,
        user: data.user || null,
        roles: data.roles || [],
        permissions: data.permissions || [],
        forbidden: false,
      });
    } catch (err) {
      console.error('Failed to load auth:', err);
      set({
        loading: false,
        authenticated: false,
        user: null,
        roles: [],
        permissions: [],
        forbidden: false,
        error: err,
      });
    }
  },

  /**
   * Check if the user has a permission
   */
  hasPermission: (perm) => {
    const { permissions, user } = get();
    return permissions.includes(perm) || user?.is_admin;
  },

  /**
   * Check if user has a role
   */
  hasRole: (role) => {
    const { roles, user } = get();
    return roles.includes(role) || user?.is_admin;
  },

  isAdmin: () => {
    const { user } = get();
    return user?.is_admin || false;
  },

  /**
   * Force unauthenticated state without calling logout endpoint.
   */
  setUnauthenticated: () => {
    set({
      loading: false,
      authenticated: false,
      user: null,
      roles: [],
      permissions: [],
      forbidden: false,
      error: null,
    });
  },

  /**
   * Check if user should have access (admin bypass or explicit permission)
   * @param roles - All roles must match (AND)
   * @param permissions - All permissions must match (AND)
   * @param rolesAny - Any role can match (OR)
   * @param permissionsAny - Any permission can match (OR)
   * @param userType - The type of user (System User or Entity User)
   */
  hasAccess: (roles, permissions, rolesAny, permissionsAny, userType = null) => {
    const { user } = get();
    
    // Admins bypass all checks
    if (user?.is_admin) return true;

    // Check user type if specified
    if (userType && user?.user_type !== userType) return false;

    // All specified roles required (AND)
    if (roles?.length && !roles.every((r) => get().hasRole(r))) return false;
    
    // All specified permissions required (AND)
    if (permissions?.length && !permissions.every((p) => get().hasPermission(p))) return false;
    
    // At least one role required (OR)
    if (rolesAny?.length && !rolesAny.some((r) => get().hasRole(r))) return false;
    
    // At least one permission required (OR)
    if (permissionsAny?.length && !permissionsAny.some((p) => get().hasPermission(p))) return false;
    
    return true;
  },

  /**
   * Logout
   */
  logout: async () => {
    try {
      await fetch('/logout', { method: 'GET', credentials: 'include' });
    } catch (e) {
      console.error('Logout failed', e);
    }
    set({
      loading: false,
      authenticated: false,
      user: null,
      roles: [],
      permissions: [],
      forbidden: false,
    });
  },
}));
